<template>
  <div class="flex flex-wrap gap-2">
    <template v-for="(tag, index) in dynamicTags" :key="index">
      <el-input
        v-if="editingIndex === index"
        :ref="(el) => { editInputRef = el }"
        v-model="editValue"
        size="small"
        style="width: 120px"
        @keyup.enter="confirmEdit(index)"
        @blur="confirmEdit(index)"
      />
      <el-tag
        v-else
        :closable="!tag.readonly"
        :disable-transitions="false"
        class="editable-tag"
        @close="handleClose(tag)"
        @click="startEdit(index, tag)"
      >
        {{ tag?.value }}
      </el-tag>
    </template>
    <el-input
      v-if="inputVisible"
      style="width: 100px"
      ref="InputRef"
      v-model="inputValue"
      size="small"
      @keyup.enter="handleInputConfirm"
      @blur="handleInputConfirm"
    />
    <el-button v-else class="button-new-tag" size="small" @click="showInput">
      + {{ t('newVariant') }}
    </el-button>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, nextTick, ref, onMounted, watch } from 'vue'
import { ElInput, ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'

// Define props
const props = defineProps({
  modelValue: Array<any>
})

const messages = {
  en: {
    newVariant: 'New Vartiant',
    duplicateTitle: 'Duplicate variant',
    duplicateBody: 'This variant already exists. Please enter a different value.'
  },
  ar: {
    newVariant: 'نسخة جديدة',
    duplicateTitle: 'نسخة مكررة',
    duplicateBody: 'هذه النسخة موجودة بالفعل. الرجاء إدخال قيمة مختلفة.'
  }
}
const { t } = useI18n({ messages, useScope: 'global' })

const inputValue = ref('')
const dynamicTags = ref([])
const inputVisible = ref(false)
const InputRef = ref<InstanceType<typeof ElInput>>()

// Inline edit state
const editingIndex = ref(-1)
const editValue = ref('')
const editInputRef = ref<InstanceType<typeof ElInput> | null>(null)

// Define emits
const emit = defineEmits(['update:modelValue', 'value-changed'])

onMounted(() => {
  dynamicTags.value = props.modelValue
})

// Case-insensitive duplicate check against existing variant values.
const normalize = (v: any) => (v ?? '').toString().trim().toLowerCase()

const isDuplicate = (value: string, excludeIndex = -1) => {
  const target = normalize(value)
  return dynamicTags.value.some(
    (tag: any, i: number) => i !== excludeIndex && normalize(tag?.value) === target
  )
}

const showDuplicateError = () => {
  ElMessage.error(t('duplicateBody'))
}

const handleClose = (tag: string) => {
  dynamicTags.value.splice(dynamicTags.value.indexOf(tag), 1)
  emit('update:modelValue', dynamicTags.value)
}

const showInput = () => {
  inputVisible.value = true
  nextTick(() => {
    InputRef.value!.input!.focus()
  })
}

const startEdit = (index: number, tag: any) => {
  editingIndex.value = index
  editValue.value = tag.value
  nextTick(() => {
    if (editInputRef.value) {
      if (editInputRef.value.input) {
        editInputRef.value.input.focus()
        editInputRef.value.input.select()
      } else {
        editInputRef.value.focus?.()
      }
    }
  })
}

const confirmEdit = (index: number) => {
  const newVal = editValue.value.trim()
  const oldVal = dynamicTags.value[index]?.value

  if (newVal && newVal !== oldVal) {
    if (isDuplicate(newVal, index)) {
      showDuplicateError()
      return
    }
    dynamicTags.value[index].value = newVal
    emit('update:modelValue', dynamicTags.value)
    emit('value-changed', { oldValue: oldVal, newValue: newVal })
  }

  editingIndex.value = -1
  editValue.value = ''
}

watch(
    () => props.modelValue,
    (newValue: any, _) => {
      dynamicTags.value = newValue
    },
    { deep: true }
)

const handleInputConfirm = () => {
  let allSpaces = true;
  for (let i = 0; i<inputValue.value.length; i++) {
    if (inputValue.value[i] != ' ') {
      allSpaces = false;
    }
  }
  if (allSpaces) {
    return;
  }
  inputValue.value = inputValue.value.trim()

  if (inputValue.value) {
    if (isDuplicate(inputValue.value)) {
      showDuplicateError()
      // Keep the input open with the entered value so the user can correct it.
      return
    }
    dynamicTags.value.push({value: inputValue.value, readonly: false})
    emit('update:modelValue', dynamicTags.value)
  }
  inputVisible.value = false
  inputValue.value = ''
  showInput();
}
</script>

<style scoped>
.editable-tag {
  cursor: pointer;
}

.editable-tag:hover {
  opacity: 0.85;
}
</style>
